import React, { useState, useEffect } from "react";
import "../App.css";
import placeholderClientLogo from "../assets/placeholderClientLogo.jpg";
import Button from "../components/Button";

function LandingPage() {
  const [blocks, setBlocks] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/content-blocks`)
      .then((res) => res.json())
      .then((data) => {
        setBlocks(data);
        setLoaded(true);
      });
  }, []);

  function get(parent, slug) {
    const block = blocks.find((b) => b.parent === parent && b.slug === slug);
    return block ? block.value : "";
  }

  if (!loaded) return <div>Loading...</div>;

  return (
    <div className="App">
      {/* SECTION 1 - Hero section */}
      <section className="hero">
        <div className="hero-content">
          <p className="eyebrow">{get("hero", "eyebrow")}</p>
          <h1>{get("hero", "heading")}</h1>
          <p className="subtitle">{get("hero", "subtitle")}</p>
          <div className="actions">
            <Button to="/enquiry">Enquire</Button>
            <Button href="#what-we-do">What we do</Button>
          </div>
        </div>
      </section>

      {/* SECTION 2 - Organisations we have worked with */}
      <section className="clients">
        <div className="clients-inner">
          <h2>{get("clients", "heading")}</h2>
          <div className="clients-grid">
            <div className="client-card">
              <img src={placeholderClientLogo} alt="Client logo" />
            </div>
            <div className="client-card">
              <img src={placeholderClientLogo} alt="Client logo" />
            </div>
            <div className="client-card">
              <img src={placeholderClientLogo} alt="Client logo" />
            </div>
            <div className="client-card">
              <img src={placeholderClientLogo} alt="Client logo" />
            </div>
            <div className="client-card">
              <img src={placeholderClientLogo} alt="Client logo" />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3 - Why multicultural markets matter */}
      <section id="why-it-matters" className="why-matters">
        <div className="why-matters-grid">
          <div className="why-matters-text">
            <h2>Why multicultural markets matter</h2>
            <p className="why-matters-lead">
              More than half of Australians come from a culturally diverse
              background, and around 1 in 4 speaks a language other than English
              at home. Most research, marketing and service design still doesn't
              account for that.
            </p>
            <p className="why-matters-subhead">
              When that happens, this is what we tend to see:
            </p>
            <ul className="why-matters-list">
              <li>
                Decisions made on assumptions about communities rather than
                evidence from them
              </li>
              <li>
                Campaigns and services that don't reach the people they were
                built for
              </li>
              <li>
                Messages that get translated accurately but still land badly
              </li>
              <li>
                Opportunities missed in communities that are growing quickly
              </li>
            </ul>
          </div>

          <div className="why-matters-stats">
            <div className="stat-card">
              <span className="stat-number">50%+</span>
              <span className="stat-label">
                of Australians come from a culturally diverse background
              </span>
            </div>
            <div className="stat-card">
              <span className="stat-number">1 in 4</span>
              <span className="stat-label">
                speaks a language other than English at home
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4 - What we do */}
      <section id="what-we-do" className="services">
        <h2>What we do</h2>
        <div className="services-grid">
          <article className="service-card">
            <h3>Research and insights</h3>
            <p className="service-intro">
              Qualitative and quantitative research with culturally and
              linguistically diverse participants.
            </p>
            <details className="service-details">
              <summary>What this includes</summary>
              <ul>
                <li>Focus groups</li>
                <li>Surveys</li>
                <li>Individual interviews</li>
                <li>Concept and product testing</li>
                <li>Campaign research and evaluation</li>
                <li>Community consultations</li>
              </ul>
              <p className="service-note">
                We recruit through community networks rather than commercial
                panels, which is usually how you reach people who don't show up
                in standard sampling.
              </p>
            </details>
          </article>

          <article className="service-card">
            <h3>Community engagement and partnerships</h3>
            <p className="service-intro">
              Direct connections with communities, built on relationships we
              already have.
            </p>
            <details className="service-details">
              <summary>What this includes</summary>
              <ul>
                <li>Introductions to community leaders and organisations</li>
                <li>Presence at community events and gatherings</li>
                <li>Sponsorships, activations and signature events</li>
                <li>Standing community advisory groups</li>
                <li>Briefings for community leaders about your organisation</li>
              </ul>
            </details>
          </article>

          <article className="service-card">
            <h3>Advisory and cultural review</h3>
            <p className="service-intro">
              Input on your plans and materials before they reach the community.
            </p>
            <details className="service-details">
              <summary>What this includes</summary>
              <ul>
                <li>
                  Reviewing materials and campaigns for cultural relevance,
                  beyond translation accuracy
                </li>
                <li>
                  Advice on how to approach a community before you approach it
                </li>
                <li>
                  Guidance on where cultural context is likely to change how a
                  message is received
                </li>
              </ul>
            </details>
          </article>
        </div>
      </section>

      {/* SECTION 5 - culturalQ */}
      <section className="culturalq">
        <div className="culturalq-inner">
          <h2>Building cultural capability in your own team</h2>
          <p>
            Some organisations want to develop the skills internally rather than
            bring someone in each time. That's what culturalQ does - cultural
            intelligence training and assessment for teams, from frontline staff
            through to senior leadership, delivered by the same organisation
            behind The New Mainstream.
          </p>
          <Button href="https://mccsa.org.au/culturalq/">
            Visit culturalQ
          </Button>
          {/* <a
            className="primary-btn"
            href="https://mccsa.org.au/culturalq/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Visit culturalQ
          </a> */}
        </div>
      </section>

      {/* SECTION 6 - Why Work With Us */}
      <section className="services">
        <h2>Why Work With Us</h2>
        <div className="services-two">
          <article className="service-card">
            <h3>We are part of MCCSA</h3>
            <p className="service-intro">
              The Multicultural Communities Council of South Australia has been
              the state's peak body for multicultural communities for more than
              50 years. The relationships behind our research took decades to
              build.
            </p>
          </article>
          <article className="service-card">
            <h3>We speak the languages</h3>
            <p className="service-intro">
              Our team works in over 20 languages, and we run sessions
              in-language where that gets better answers.
            </p>
          </article>
          <article className="service-card">
            <h3>We know the protocols</h3>
            <p className="service-intro">
              How you approach a community matters as much as what you ask it.
              We handle that part.
            </p>
          </article>
          <article className="service-card">
            <h3>We do the research properly</h3>
            <p className="service-intro">
              Cultural knowledge and professional research method, not one at
              the expense of the other.
            </p>
          </article>
        </div>
        <br />
        <div className="services-column">
          <article className="service-card">
            <h3>Communities benefit too</h3>
            <p className="service-intro">
              We work in ways that give something back to the people who take
              part, not only to the organisations commissioning the work.
            </p>
          </article>
        </div>
      </section>

      {/* SECTION 7 - What working with us looks like */}
      <section className="services">
        <h2>What working with us looks like</h2>

        <p>
          Every project is different, so this is a rough shape rather than a
          fixed process.
        </p>

        <div className="services-row">
          <article className="service-card">
            <h3>1. A conversation</h3>
            <p className="service-intro">
              You tell us what you're trying to find out or who you're trying to
              reach. We tell you honestly whether we can help.
            </p>
          </article>
          <article className="service-card">
            <h3>2. A proposal</h3>
            <p className="service-intro">
              Scope, method, communities, timeline and cost, written for your
              sign-off.
            </p>
          </article>
          <article className="service-card">
            <h3>3. The work</h3>
            <p className="service-intro">
              We recruit, run and manage it, and keep you posted as we go.
            </p>
          </article>
          <article className="service-card">
            <h3>4. What we found</h3>
            <p className="service-intro">
              Findings and recommendations you can act on, and a debrief with
              your team if that's useful.
            </p>
          </article>
        </div>
        <br />
        <p>
          We are happy to work as a subcontractor to research and communications
          agencies as well as directly with clients.
        </p>
      </section>

      {/* SECTION 8 - Research */}
      <div className="panel-page" id="research-panel">
        <section className="community-banner">
          <div className="community-banner-content">
            <div className="eyebrow eyebrow-alt">Community Engagement</div>
            <h2>Join our research panel</h2>
            <p className="community-banner-text">
              We are always looking for people from all cultural backgrounds to
              take part in focus groups, surveys and interviews. It's a way to
              make sure your community is part of the decisions being made about
              it.
            </p>
            <nav className="actions">
              <a className="light-btn" href="/panel-info">
                Find out more
              </a>
            </nav>
          </div>
        </section>
      </div>
      <br />
    </div>
  );
}

export default LandingPage;
